/**
 * Retry policy (P0-WEB-04).
 *
 * The rule that matters is the one about which methods: **a write is never
 * retried.** Every operation in this product is a run (ADR-0007) and a run
 * costs credits, so a `POST` replayed because a gateway was slow to answer
 * charges twice for one intent. P0-PLAT-16 has idempotency keys, but they cover
 * run creation only — they are not a licence to retry every write.
 *
 * Reads are safe and worth retrying, because the failure being papered over is
 * a gateway restart or a dropped connection, not a decision the server made.
 */

/** Methods with no side effect, so replaying one cannot change anything. */
const IDEMPOTENT_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD']);

/**
 * Statuses worth a second attempt.
 *
 * 500 is deliberately absent. A 500 is a deterministic bug on a code path the
 * request just took; retrying it produces the same 500 a second later, and all
 * the retry buys is a slower error and a second entry in the log.
 */
const RETRYABLE_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

/** Two retries, so a transient blip costs at most ~1.2 s before it surfaces. */
export const MAX_RETRIES = 2;

export interface RetryDecision {
  readonly method: string;
  /** Absent when the request never got a response. */
  readonly status: number | undefined;
  /** How many retries have already been spent. */
  readonly attempt: number;
  readonly maxRetries?: number;
}

export function shouldRetry({
  method,
  status,
  attempt,
  maxRetries = MAX_RETRIES,
}: RetryDecision): boolean {
  if (attempt >= maxRetries) return false;
  if (!IDEMPOTENT_METHODS.has(method.toUpperCase())) return false;

  // No status: the transport failed. Worth retrying for a read — a single
  // dropped connection is the common case and it recovers immediately.
  if (status === undefined) return true;

  return RETRYABLE_STATUSES.has(status);
}

/**
 * Exponential backoff with full jitter: ~300 ms, then ~900 ms.
 *
 * Jittered because the portal fires several loaders at once. Without it, a
 * gateway that dropped four parallel requests receives all four retries in the
 * same millisecond, which is the load pattern that kept it down.
 */
export function backoffMs(attempt: number, random: () => number = Math.random): number {
  const ceiling = 300 * 3 ** attempt;
  return Math.round(ceiling * (0.5 + random() * 0.5));
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
